/** SessionManager. Owns lifecycle state machine + audio unlock + idle timer. Never touches audio params or visual uniforms. */

import type { StateName } from '../types';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Idle threshold before first onIdle fires (ms). Spec §7. */
const IDLE_THRESHOLD_MS = 8000;
/** Cadence for subsequent onIdle fires after threshold. 78bpm → 60_000/78 * 4 = 3076.92 */
const IDLE_BAR_MS = 3077;

// ─── Legal transitions ────────────────────────────────────────────────────────

const LEGAL_TRANSITIONS: ReadonlyMap<StateName, ReadonlySet<StateName>> = new Map([
  ['LOADING',       new Set<StateName>(['AWAITING_TAP'])],
  ['AWAITING_TAP',  new Set<StateName>(['PLAYING'])],
  ['PLAYING',       new Set<StateName>(['SHARE_GATE', 'INTERRUPTED'])],
  ['SHARE_GATE',    new Set<StateName>(['PLAYING', 'INTERRUPTED'])],
  ['INTERRUPTED',   new Set<StateName>(['PLAYING'])],
]);

// ─── Exports ──────────────────────────────────────────────────────────────────

export interface SessionManagerDeps {
  audioContext: AudioContext;
  /** Called inside the first user gesture. Must call Tone.start() + unmuteIOSAudio(). */
  unlockAudio: () => Promise<void>;
  /** Returns a high-resolution monotonic time in ms (typically performance.now). */
  now: () => number;
}

export interface SessionManagerEvents {
  onStateChange: (state: StateName, prev: StateName) => void;
  /** Fires when idle threshold (spec §7: 8s) crosses + every bar after. */
  onIdle: (idleMs: number) => void;
}

export class IllegalTransitionError extends Error {
  readonly from: StateName;
  readonly to: StateName;

  constructor(from: StateName, to: StateName) {
    super(`Illegal state transition: ${from} → ${to}`);
    this.name = 'IllegalTransitionError';
    this.from = from;
    this.to = to;
  }
}

// ─── SessionManager ───────────────────────────────────────────────────────────

export class SessionManager {
  private _state: StateName = 'LOADING';
  private readonly _deps: SessionManagerDeps;

  private _events: SessionManagerEvents = {
    onStateChange: () => {},
    onIdle:        () => {},
  };

  // Idle timer state
  private _lastInputAtMs: number;
  private _lastIdleFireAtMs = -Infinity;
  private _firstTapPromise: Promise<void> | null = null;

  // Bound event handlers for listener cleanup
  private readonly _onVisibility: () => void;
  private readonly _onAcStateChange: () => void;

  constructor(deps: SessionManagerDeps) {
    this._deps = deps;
    this._lastInputAtMs = deps.now();

    this._onVisibility    = this._handleVisibility.bind(this);
    this._onAcStateChange = this._handleAcStateChange.bind(this);

    document.addEventListener('visibilitychange', this._onVisibility);
    deps.audioContext.addEventListener('statechange', this._onAcStateChange);
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  get state(): StateName {
    return this._state;
  }

  on(events: Partial<SessionManagerEvents>): void {
    if (events.onStateChange !== undefined) this._events.onStateChange = events.onStateChange;
    if (events.onIdle !== undefined)        this._events.onIdle        = events.onIdle;
  }

  markReady(): void {
    this._transition('AWAITING_TAP');
  }

  async handleTap(): Promise<void> {
    if (this._state === 'AWAITING_TAP') {
      if (this._firstTapPromise) return this._firstTapPromise;

      // unlockAudio MUST be called synchronously inside the gesture — start the
      // promise synchronously, then await it.
      const unlock = this._deps.unlockAudio();
      this._firstTapPromise = (async () => {
        try {
          await unlock;
          if (this._state === 'AWAITING_TAP') {
            this._transition('PLAYING');
          }
        } finally {
          this._firstTapPromise = null;
        }
      })();
      return this._firstTapPromise;
    } else if (this._state === 'INTERRUPTED') {
      const resume = this._deps.audioContext.resume();
      await resume;
      this._transition('PLAYING');
    } else if (this._state === 'PLAYING') {
      const acState = this._deps.audioContext.state as string;
      if (acState !== 'running') {
        await this._deps.audioContext.resume();
        return;
      }
      throw new IllegalTransitionError(this._state, 'PLAYING');
    } else {
      throw new IllegalTransitionError(this._state, 'PLAYING');
    }
  }

  enterShareGate(): void {
    this._transition('SHARE_GATE');
  }

  exitShareGate(): void {
    this._transition('PLAYING');
  }

  tick(now: number, anyInputThisFrame: boolean): void {
    if (anyInputThisFrame) {
      this._lastInputAtMs = now;
      return;
    }

    const idleMs = now - this._lastInputAtMs;

    if (idleMs < IDLE_THRESHOLD_MS) return;

    // First crossing of the threshold
    if (this._lastIdleFireAtMs < this._lastInputAtMs) {
      this._events.onIdle(idleMs);
      this._lastIdleFireAtMs = now;
      return;
    }

    // Subsequent fires every IDLE_BAR_MS
    if (now - this._lastIdleFireAtMs >= IDLE_BAR_MS) {
      this._events.onIdle(idleMs);
      this._lastIdleFireAtMs = now;
    }
  }

  destroy(): void {
    document.removeEventListener('visibilitychange', this._onVisibility);
    this._deps.audioContext.removeEventListener('statechange', this._onAcStateChange);
  }

  // ─── Private ─────────────────────────────────────────────────────────────────

  private _transition(to: StateName): void {
    const from = this._state;
    const allowed = LEGAL_TRANSITIONS.get(from);
    if (!allowed || !allowed.has(to)) {
      throw new IllegalTransitionError(from, to);
    }
    this._state = to;
    this._events.onStateChange(to, from);
  }

  private _handleVisibility(): void {
    if (document.visibilityState === 'hidden') {
      if (this._state === 'PLAYING' || this._state === 'SHARE_GATE') {
        this._transition('INTERRUPTED');
      }
    }
  }

  private _handleAcStateChange(): void {
    const acState = this._deps.audioContext.state as string;
    if (acState === 'suspended' || acState === 'interrupted') {
      if (this._state === 'PLAYING' || this._state === 'SHARE_GATE') {
        this._transition('INTERRUPTED');
      }
    }
  }
}
